import { deconstructPairId, getGeolocationKey, memoizeWithTimeout } from '../../utils';
import { SwapMode } from '../../common/types';
import { BASE_URL, DEFAULT_FEE_ADDRESS, DEFAULT_FEE_BPS, DEFAULT_SLIPPAGE, MAX_AMOUNT_USD, MIN_AMOUNT_USD, NETWORK_TO_RANGO_BLOCKCHAIN, RANGO_BLOCKCHAIN_TO_NETWORK, SVC, } from './constants';
import decodeTransaction, { getSpenderAddress, } from './utils/decode-transaction';
import { ETH_NULL_ADDRESS, ZERO_ALLOWANCE_ASSETS } from '../../constants';
import { APPROVAL_GAS_LIMIT, EXACT_OUT_ALLOWANCE_TRESHOLD, QUOTE_CACHE_TTL } from '../constants';
import { parseFetchivalError, serializeQuoteKey } from '../utils';
import { ApproveTokensError, CreateDexTxError, CreateOrderError, CreateTxError, ExchangeError, GetAllowanceError, OrderValidationError, SendTxError, } from '../../errors';
class Rango {
    name = 'rango';
    _baseUrl;
    _fetchival;
    _feeAddress;
    _feeBps;
    _apiKeys;
    _apiKey;
    _assetsService;
    _ordersService;
    _pricingService;
    _cachedUsdPrices = new Map();
    _cachedQuotes = new Map();
    _tracer;
    constructor(params) {
        const { fetchival, apiKeys, feeAddress, feePercentage, ordersService, pricingService, assetsService, baseUrl = BASE_URL, tracer, } = params;
        this._baseUrl = new URL(baseUrl);
        this._feeAddress = feeAddress || DEFAULT_FEE_ADDRESS;
        this._feeBps = feePercentage ? feePercentage * 100 : DEFAULT_FEE_BPS;
        this._apiKeys = apiKeys;
        this._fetchival = fetchival;
        this._assetsService = assetsService;
        this._ordersService = ordersService;
        this._pricingService = pricingService;
        this._apiKey = this._apiKeys.default;
        this._tracer = tracer;
    }
    async getSupportedFromAssetIds() {
        const tokens = await this._getTokens();
        return new Set(tokens.map((token) => token.id));
    }
    async getSupportedToAssetIds(from) {
        const tokens = await this.getSupportedFromAssetIds();
        if (!tokens.has(from)) {
            return new Set();
        }
        const tokensWithoutFrom = new Set(tokens);
        tokensWithoutFrom.delete(from);
        return tokens.has(from) ? tokensWithoutFrom : new Set();
    }
    async isPairSupported(pairId) {
        try {
            const { from, to } = deconstructPairId(pairId);
            const [fromAsset, toAsset] = await Promise.all([
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
            ]);
            if (fromAsset?.network !== toAsset?.network) {
                return false;
            }
            const tokens = await this.getSupportedFromAssetIds();
            return tokens.has(from) && tokens.has(to);
        }
        catch {
            return false;
        }
    }
    async getQuote(params) {
        const { pairId, amount, fromAddress, slippage, swapMode = SwapMode.ExactIn } = params;
        const quoteCacheKey = serializeQuoteKey({ pairId, amount, fromAddress, slippage, swapMode });
        const cachedQuote = this._cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || Date.now() > cachedQuote.expiry) {
            const quote = await this._getQuote(params);
            this._cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this._cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { amount, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, pairId } = params;
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new Error('missing pair');
        }
        const [response, limits] = await Promise.all([
            this._getQuoteFromRango({
                amount: params.amount,
                fromAsset,
                toAsset,
                swapMode,
                slippage,
            }),
            this.getLimits({ pairId: params.pairId }),
        ]);
        const limit = swapMode === SwapMode.ExactIn ? limits[0]?.ExactIn : limits[0]?.ExactOut;
        if (!limit || amount.gt(limit.max) || amount.lt(limit.min)) {
            const { value: minValue = 'UNDEF_VAL' } = limit?.min || {};
            const { value: maxValue = 'UNDEF_VAL' } = limit?.max || {};
            const err = new Error(`No rate found for ${amount.toDefaultString()} (min: ${minValue} - max: ${maxValue})`);
            err.name = 'NO_RATE_AVAILABLE';
            throw err;
        }
        const gasLimit = 500_000;
        const minerFee = toAsset.currency.defaultUnit(0);
        const quoteAmount = swapMode === SwapMode.ExactIn
            ? toAsset.currency.baseUnit(response.route.outputAmount)
            : fromAsset.currency.baseUnit(response.route.path[0]?.inputAmount);
        const rate = swapMode === SwapMode.ExactIn
            ? quoteAmount.div(params.amount.toDefaultString()).toDefaultString()
            : params.amount.div(quoteAmount.toDefaultString()).toDefaultString();
        return {
            providerName: this.name,
            rateId: response.requestId,
            amount: quoteAmount,
            minerFee,
            rate,
            exchangeFeeData: {
                feeOpts: {
                    gasLimit: gasLimit + APPROVAL_GAS_LIMIT,
                },
            },
            exchangeTxData: {},
        };
    }
    async prepareExchange(params) {
        try {
            const traceMessage = this._traceMessageScope('prepareExchange');
            const { anonymousId, fromAddress, fromAmount, toAmount, onOrderIdGenerated, pairId, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, toAddress, createTx, referralCode, quote, } = params;
            const { from, to } = deconstructPairId(params.pairId);
            traceMessage('Creating order in the exchange-server');
            this._tracer?.traceTime('LogDexOrder');
            const orderPayload = {
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                fromAddress,
                toAddress,
                toAmount: toAmount.toDefaultString({ unit: false }),
                pairId,
                slippage: slippage || DEFAULT_SLIPPAGE,
                svc: SVC,
                referralCode,
                swapMode,
            };
            const [fromAsset, toAsset, order] = await Promise.all([
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
                this._ordersService
                    .insertDex(orderPayload, { anonymousId })
                    .catch(async (err) => {
                    const errorMessage = await parseFetchivalError(err);
                    throw new CreateOrderError({
                        message: errorMessage,
                        details: orderPayload,
                    });
                })
                    .finally(() => this._tracer?.traceTimeEnd('LogDexOrder')),
            ]);
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            if (!fromAsset || !toAsset) {
                throw new Error('missing pair');
            }
            traceMessage('Getting tx from rango');
            this._tracer?.traceTime('GetDexSwapTx');
            const swapPayload = {
                apiKey: this._apiKey,
                amount: fromAmount.toBaseString({ unit: false }),
                from: this._getRangoIdForAsset(fromAsset),
                slippage: slippage || DEFAULT_SLIPPAGE,
                fromAddress,
                to: this._getRangoIdForAsset(toAsset),
                toAddress,
                referrerFee: this._feeBps / 100,
                referrerAddress: this._feeAddress,
                disableEstimate: true,
            };
            const response = await this._request('basic/swap')
                .get(swapPayload)
                .catch(async (err) => {
                const errorMessage = await parseFetchivalError(err);
                throw new CreateDexTxError({
                    message: errorMessage,
                    details: swapPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetDexSwapTx'));
            if (response.error) {
                traceMessage(`Failed to get tx from Rango. Payload: ${JSON.stringify(swapPayload)}\nError: ${response.error}`);
                throw new CreateDexTxError({
                    message: response.error || 'Unknown error from Rango API',
                    details: swapPayload,
                });
            }
            traceMessage('Decoding Rango transaction');
            const transactionData = decodeTransaction(response.tx.txData, fromAddress);
            traceMessage('Checking order validity');
            this._tracer?.traceTime('CheckOrderIsValid');
            await this._checkOrderIsValid({ order, rangoTx: transactionData })
                .catch((err) => {
                throw new OrderValidationError({
                    message: err.message,
                    order,
                    providerOrder: transactionData,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            traceMessage('Creating TX');
            this._tracer?.traceTime('CreateTx');
            const createTxParams = {
                assetId: fromAsset.id,
                txData: {
                    chainId: Number(response.tx.blockChain.chainId),
                    data: response.tx.txData,
                    from: response.tx.from,
                    gasLimit: quote.exchangeFeeData.feeOpts.gasLimit,
                    gasPrice: quote.exchangeFeeData.feeOpts?.gasPrice,
                    tipGasPrice: quote.exchangeFeeData.feeOpts?.tipGasPrice,
                    to: response.tx.txTo,
                    value: response.tx.value,
                },
                isSendAll: params.isSendAll,
            };
            const transaction = await createTx(createTxParams)
                .catch((err) => {
                throw new CreateTxError({
                    message: err.message,
                    details: createTxParams,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateTx'));
            const providerOrderId = response.requestId;
            const spenderAddress = response.tx.approveData && getSpenderAddress(response.tx.approveData, fromAddress);
            return {
                approvalAddress: spenderAddress,
                providerOrderId,
                orderId: order.id,
                transaction,
                fromAmount: swapMode === SwapMode.ExactIn
                    ? fromAmount
                    : fromAmount.mul(1 + slippage / 100 + EXACT_OUT_ALLOWANCE_TRESHOLD),
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ pairId, fromAmount, transaction, orderId, providerOrderId, approvalAddress, getAllowance, approveTokens, sendTx, }) {
        try {
            const traceMessage = this._traceMessageScope('executeExchange');
            if (!providerOrderId) {
                throw new Error('missing providerOrderId');
            }
            const { from } = deconstructPairId(pairId);
            const fromAsset = await this._assetsService.getOneById(from);
            if (!fromAsset) {
                throw new Error('missing pair');
            }
            const transactions = [];
            if (approvalAddress) {
                traceMessage('Checking allowance');
                this._tracer?.traceTime('GetAllowance');
                const allowance = await getAllowance({
                    approvalAddress,
                    assetId: fromAsset.id,
                })
                    .catch((err) => {
                    throw new GetAllowanceError({
                        message: err.message,
                        assetId: fromAsset.id,
                        approvalAddress,
                    });
                })
                    .finally(() => this._tracer?.traceTimeEnd('GetAllowance'));
                if (allowance.lt(fromAmount)) {
                    if (allowance.isPositive && ZERO_ALLOWANCE_ASSETS.includes(fromAsset.id)) {
                        traceMessage('Revoking allowance');
                        this._tracer?.traceTime('ApproveTokens');
                        const revokeApproveTxId = await approveTokens({
                            assetId: fromAsset.id,
                            approveAmount: fromAsset.currency.ZERO,
                            spenderAddress: approvalAddress,
                        })
                            .catch((err) => {
                            throw new ApproveTokensError({
                                message: err.message,
                                assetId: fromAsset.id,
                                approvalAddress,
                                amount: fromAsset.currency.ZERO,
                            });
                        })
                            .finally(() => this._tracer?.traceTimeEnd('ApproveTokens'));
                        transactions.push({ type: 'preSetupTransaction', id: revokeApproveTxId });
                    }
                    traceMessage('Approving tokens');
                    this._tracer?.traceTime('ApproveTokens');
                    const approveTxId = await approveTokens({
                        assetId: fromAsset.id,
                        approveAmount: fromAmount,
                        spenderAddress: approvalAddress,
                    })
                        .catch((err) => {
                        throw new ApproveTokensError({
                            message: err.message,
                            assetId: fromAsset.id,
                            approvalAddress,
                            amount: fromAmount,
                        });
                    })
                        .finally(() => this._tracer?.traceTimeEnd('ApproveTokens'));
                    transactions.push({ type: 'setupTransaction', id: approveTxId });
                }
            }
            traceMessage('Sending TX');
            this._tracer?.traceTime('SendTx');
            const { txId } = await sendTx(transaction)
                .catch((err) => {
                throw new SendTxError({
                    message: err.message,
                    details: transaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('SendTx'));
            transactions.push({ type: 'swapTransaction', id: txId });
            traceMessage('Updating order with txId');
            this._ordersService
                .updateDex({
                id: orderId,
                transactionId: txId,
                providerOrderId,
            })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return { transactions, svc: SVC, providerOrderId };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async getLimits(params) {
        try {
            const { from, to } = deconstructPairId(params.pairId);
            const [fromAsset, toAsset, usdPrices] = await Promise.all([
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
                this._pricingService.getPrices([from, to]),
            ]);
            if (!fromAsset || !toAsset) {
                throw new Error('missing pair');
            }
            const fromTokenUsdPrice = usdPrices[from];
            const toTokenUsdPrice = usdPrices[to];
            if (!fromTokenUsdPrice || !toTokenUsdPrice) {
                throw new Error(`Missing usd price for ${fromTokenUsdPrice ? to : from}`);
            }
            const exactInMinAmount = MIN_AMOUNT_USD / fromTokenUsdPrice;
            const exactInMaxAmount = MAX_AMOUNT_USD / fromTokenUsdPrice;
            const exactOutMinAmount = MIN_AMOUNT_USD / toTokenUsdPrice;
            const exactOutMaxAmount = MAX_AMOUNT_USD / toTokenUsdPrice;
            return [
                {
                    [SwapMode.ExactIn]: {
                        min: fromAsset.currency.defaultUnit(exactInMinAmount),
                        max: fromAsset.currency.defaultUnit(exactInMaxAmount),
                    },
                    [SwapMode.ExactOut]: {
                        min: toAsset.currency.defaultUnit(exactOutMinAmount),
                        max: toAsset.currency.defaultUnit(exactOutMaxAmount),
                    },
                },
            ];
        }
        catch {
            return [];
        }
    }
    async _getUsdPrice(from) {
        if (this._cachedUsdPrices.size === 0) {
            await this._getTokens();
        }
        return this._cachedUsdPrices.get(from);
    }
    setGeolocation(geolocation) {
        if (!geolocation) {
            this._apiKey = this._apiKeys.default;
            return;
        }
        const geolocationKey = getGeolocationKey(geolocation);
        if (this._apiKeys.hasOwnProperty(geolocationKey)) {
            this._apiKey = this._apiKeys[geolocationKey];
        }
    }
    _getQuoteFromRango = async (params) => {
        const { amount, fromAsset, toAsset, swapMode = SwapMode.ExactIn, slippage } = params;
        if (swapMode === SwapMode.ExactOut) {
            const limits = await this.getLimits({ pairId: `${fromAsset.id}_${toAsset.id}` });
            const fromAmount = limits[0].ExactIn.min;
            const quote = await this._getQuoteFromRango({
                ...params,
                amount: fromAmount,
                swapMode: SwapMode.ExactIn,
            });
            const toAmount = toAsset.currency.baseUnit(quote.route.outputAmount);
            const rate = toAmount.div(fromAmount.toDefaultString()).toDefaultString();
            const estimatedFromAmount = fromAsset.currency
                .defaultUnit(amount.div(rate).toDefaultString())
                .mul(1 + slippage / 100);
            return this._getQuoteFromRango({
                ...params,
                amount: estimatedFromAmount,
                swapMode: SwapMode.ExactIn,
            });
        }
        const baseAmount = amount.toBaseString({ unit: false });
        const rangoFromAsset = this._getRangoIdForAsset(fromAsset);
        const rangoToAsset = this._getRangoIdForAsset(toAsset);
        const response = await this._request('basic/quote').get({
            amount: baseAmount,
            apiKey: this._apiKey,
            from: rangoFromAsset,
            to: rangoToAsset,
            referrerFee: this._feeBps / 100,
            slippage,
        });
        if (response.resultType !== 'OK' || response.route.path.length === 0) {
            throw new Error('Getting quote from Rango API failed');
        }
        return response;
    };
    _getTokens = memoizeWithTimeout(async () => {
        const responsePromise = this._request('basic/meta').get({
            apiKey: this._apiKey,
            transactionTypesExclude: true,
        });
        const assetsByNetworksPromise = this._assetsService.getAllByNetworks([
            ...RANGO_BLOCKCHAIN_TO_NETWORK.values(),
        ]);
        const [response, assetsByNetworks] = await Promise.all([
            responsePromise,
            assetsByNetworksPromise,
        ]);
        const getCacheKey = (params) => `${params.network}-${params.address}`;
        const assetsInNetworkCache = new Map();
        const getAssetsInNetworkCache = (network, assetsInNetwork) => {
            const existingCache = assetsInNetworkCache.get(network);
            if (existingCache)
                return existingCache;
            const cache = new Map();
            const assets = [...assetsInNetwork.values()];
            assets.forEach((asset) => {
                if (!asset)
                    return;
                const cacheKey = getCacheKey({
                    network: asset.network,
                    address: asset.meta?.contractAddress?.toLowerCase() ?? '',
                });
                cache.set(cacheKey, asset);
            });
            assetsInNetworkCache.set(network, cache);
            return cache;
        };
        return response.tokens.reduce((acc, token) => {
            const network = RANGO_BLOCKCHAIN_TO_NETWORK.get(token.blockchain) ?? '';
            const assetsInNetwork = assetsByNetworks.get(network);
            if (!assetsInNetwork) {
                return acc;
            }
            const assetsInNetworkCache = getAssetsInNetworkCache(network, assetsInNetwork);
            let asset;
            const cackeKey = getCacheKey({
                network: RANGO_BLOCKCHAIN_TO_NETWORK.get(token.blockchain),
                address: token.address?.toLowerCase() ?? '',
            });
            if (assetsInNetworkCache.has(cackeKey)) {
                asset = assetsInNetworkCache.get(cackeKey);
            }
            if (asset) {
                this._cachedUsdPrices.set(asset.id, token.usdPrice);
                acc.push(asset);
                return acc;
            }
            return acc;
        }, []);
    });
    _getRangoIdForAsset(asset) {
        const rangoBlockchain = NETWORK_TO_RANGO_BLOCKCHAIN.get(asset.network);
        if (!rangoBlockchain) {
            throw new Error(`missing blockchain for ${asset.network} network`);
        }
        if (asset.meta?.contractAddress) {
            return `${rangoBlockchain}.${asset.symbol}--${asset.meta.contractAddress}`;
        }
        return `${rangoBlockchain}.${asset.symbol}`;
    }
    async _checkOrderIsValid(params) {
        const { order, rangoTx } = params;
        const { from, to } = deconstructPairId(order.pairId);
        const fromAsset = await this._assetsService.getOneById(from);
        const toAsset = await this._assetsService.getOneById(to);
        if (!fromAsset || !toAsset) {
            throw new Error('missing pair');
        }
        const fromTokenAddress = fromAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const toTokenAddress = toAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const amountFromOrder = fromAsset.currency
            .defaultUnit(order.amount.value)
            .toBaseString({ unit: false });
        if (amountFromOrder !== rangoTx.amount) {
            throw new Error(`Invalid Order: ${amountFromOrder} !== ${rangoTx.amount}`);
        }
        if (rangoTx.receiver.toLowerCase() !== order.toAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${rangoTx.receiver.toLowerCase()} !== ${order.toAddress.toLowerCase()}`);
        }
        if (rangoTx.fromToken.toLowerCase() !== fromTokenAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${rangoTx.fromToken.toLowerCase()} !== ${fromTokenAddress.toLowerCase()}`);
        }
        if (rangoTx.toToken.toLowerCase() !== toTokenAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${rangoTx.toToken.toLowerCase()} !== ${toTokenAddress.toLowerCase()}`);
        }
    }
    _request(path) {
        const url = new URL(this._baseUrl);
        url.pathname = path;
        return this._fetchival(url);
    }
    _traceMessageScope(scope) {
        return (message) => this._tracer?.traceMessage(`${this.name}:${scope}:${message}`);
    }
}
export default Rango;
